/**
 * Zod schemas for the session domain's wire shapes, served by
 * tricorder-session through the gateway's /sessions/** proxy from the
 * cut-over on. Mirrors types_session.ts (tricorder-session SPEC.md
 * sections 2, 3, 7).
 */

import { z } from 'zod';

const ActorRefSchema = z.object({
  performer: z.string(),
  on_behalf_of: z.string().nullable().optional(),
});

const PinnedAgentSchema = z.object({
  slug: z.string(),
  version: z.string(),
});

const SubjectRefSchema = z.object({
  type: z.string(),
  id: z.string(),
});

export const SessionResponseSchema = z.object({
  id: z.string(),
  // Absent on a session that pins no agent: a protocol run
  // (tricorder-session/ADR-0009), which tricorder-session serves without it.
  agent: PinnedAgentSchema.optional(),
  subject: SubjectRefSchema.nullable().optional(),
  parent: z.string().nullable().optional(),
  status: z.enum(['active', 'archived']),
  title: z.string(),
  summary: z.string(),
  created_by: ActorRefSchema,
  created_at: z.string(),
  updated_at: z.string(),
  archived_at: z.string().nullable().optional(),
});

export const MessageResponseSchema = z.object({
  id: z.string(),
  session: z.string(),
  seq: z.number(),
  type: z.string(),
  role: z.enum(['user', 'agent']).nullable().optional(),
  author: ActorRefSchema,
  payload: z.unknown(),
  created_at: z.string(),
});

function sessionPageSchema<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    items: z.array(item),
  });
}

export const SessionResponsePageSchema = sessionPageSchema(SessionResponseSchema);
export const MessageResponsePageSchema = sessionPageSchema(MessageResponseSchema);

export const CreateSessionResponseSchema = z.object({
  session: SessionResponseSchema,
  first_message: MessageResponseSchema,
});

// An agentless create's 201 (tricorder-session/ADR-0009): the session, and no
// first message, because no turn runs on it.
export const CreateAgentlessSessionResponseSchema = z.object({
  session: SessionResponseSchema,
});

export const SessionSummaryResponseSchema = z.object({
  session: z.string(),
  summary: z.string(),
});

const LayerVersionSchema = z.object({
  name: z.string(),
  version: z.string(),
});

export const PromptPreviewResponseSchema = z.object({
  agent: z.string(),
  version: z.string(),
  rendered: z.string(),
  layers: z.array(LayerVersionSchema),
});

export const PromptRecordResponseSchema = z.object({
  id: z.string(),
  session: z.string(),
  seq: z.number(),
  author: ActorRefSchema,
  rendered: z.string(),
  layers: z.array(LayerVersionSchema),
  created_at: z.string(),
});

export const PromptRecordPageSchema = sessionPageSchema(PromptRecordResponseSchema);

export const SessionTraceResponseSchema = z.object({
  id: z.string(),
  session: z.string(),
  seq: z.number(),
  kind: z.string(),
  detail: z.unknown(),
  latency_ms: z.number().nullable().optional(),
  created_at: z.string(),
});

export const SessionTracePageSchema = sessionPageSchema(SessionTraceResponseSchema);

export const SessionFeedbackResponseSchema = z.object({
  id: z.string(),
  session: z.string(),
  message: z.string(),
  author: ActorRefSchema,
  type: z.string(),
  text: z.string().optional(),
  created_at: z.string(),
});

export const SessionFeedbackPageSchema = sessionPageSchema(SessionFeedbackResponseSchema);

export const SessionShareResponseSchema = z.object({
  principal_type: z.string(),
  principal_id: z.string(),
  role: z.string(),
  expires_at: z.string().nullable().optional(),
});

export const SessionShareListSchema = z.object({
  items: z.array(SessionShareResponseSchema),
});

export const SessionElicitationResponseSchema = z.object({
  id: z.string(),
  session: z.string(),
  seq: z.number(),
  tool_call: z.unknown(),
  state: z.string(),
  answer: z.unknown().optional(),
  created_at: z.string(),
  answered_at: z.string().nullable().optional(),
});

export const SessionElicitationPageSchema = sessionPageSchema(SessionElicitationResponseSchema);

// A com.ai4nr.session.message.turn_failure payload (tricorder-protos
// schemas/session/turn_failure.cue, tricorder-session/ADR-0019): closed, as the
// CUE definition is, so a record of another shape is refused, never guessed at.
export const TURN_FAILURE_KINDS = [
  'agent_unavailable',
  'run_failed',
  'timed_out',
  'tool_unreachable',
  'quota_exceeded',
  'no_answer',
  'tool_loop_aborted',
  'request_blocked',
  'account_unknown',
  'subject_unknown',
  'access_refused',
  'unreadable',
] as const;

// tool is present exactly for tool_unreachable, and outcome at most for
// no_answer, as the CUE definition's conditionals say.
export const TurnFailurePayloadSchema = z
  .strictObject({
    seq: z.number().int().min(1),
    failure: z.enum(TURN_FAILURE_KINDS),
    reason: z.string().min(1),
    options: z.array(z.string().min(1)).min(1),
    language: z.string().regex(/^[a-z]{2,3}$/),
    tool: z.string().min(1).optional(),
    outcome: z.enum(['blocked', 'truncated', 'tool_call_failed']).optional(),
  })
  .refine((p) => (p.failure === 'tool_unreachable') === (p.tool !== undefined), {
    path: ['tool'],
    message: 'tool is present exactly for tool_unreachable',
  })
  .refine((p) => p.outcome === undefined || p.failure === 'no_answer', {
    path: ['outcome'],
    message: 'outcome is present only for no_answer',
  });

// A step message's payload (tricorder-protos schemas/protocol/step.cue at
// v1.20.0): the signed state token is the state; the ids beside it are a
// reader's view.
export const ProtocolStepPayloadSchema = z.object({
  state_token: z.string().min(1),
  from_node_id: z.string().optional(),
  to_node_id: z.string(),
  action_id: z.string().optional(),
  protocol_id: z.string().optional(),
  protocol_version: z.string().optional(),
  observation: z.record(z.string(), z.union([z.number(), z.string(), z.boolean()])).optional(),
});
